<template>
  <section
    class="mt-6 flex w-full flex-col gap-4 rounded-lg bg-white p-5 dark:bg-gray-800"
    dir="rtl"
  >
    <div>
      <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">متوسط درجات الحلقات</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        متوسط درجات الحفظ والمراجعة المسجلة لكل حلقة خلال الفترة المحددة.
      </p>
    </div>

    <canvas
      ref="chartCanvas"
      class="max-h-80"
      :class="{ hidden: sessionAverages.length === 0 }"
    />
    <p
      v-if="sessionAverages.length === 0"
      class="py-8 text-center text-sm text-gray-500 dark:text-gray-400"
    >
      لا توجد درجات مسجلة خلال الفترة المحددة.
    </p>
  </section>
</template>

<script setup lang="ts">
import { Chart, type ChartData } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { computed, onMounted, shallowRef, useTemplateRef, watch } from 'vue'
import type { Evaluation, Session } from '@/types/global'

const props = defineProps<{
  sessions: Session[]
  evaluations: Evaluation[]
}>()

const chartCanvas = useTemplateRef<HTMLCanvasElement>('chartCanvas')
const chart = shallowRef<Chart | null>(null)
const scoreFields = ['degree_memorize', 'degree_review'] as const

const sessionAverages = computed(() => {
  const scoresBySession = new Map<number, number[]>()

  for (const evaluation of props.evaluations) {
    const sessionID = evaluation.session_id?.id
    if (!sessionID) continue

    for (const field of scoreFields) {
      const value = evaluation[field]
      if (value === false || value === null || value === undefined || value === '') continue

      const score = Number(value)
      if (!Number.isFinite(score)) continue

      const scores = scoresBySession.get(sessionID) ?? []
      scores.push(score)
      scoresBySession.set(sessionID, scores)
    }
  }

  return props.sessions.flatMap((session) => {
    const scores = scoresBySession.get(session.id) ?? []
    if (scores.length === 0) return []

    return [{
      id: session.id,
      name: session.name,
      average: scores.reduce((sum, score) => sum + score, 0) / scores.length,
      count: scores.length
    }]
  })
})

function chartData(): ChartData<'bar'> {
  return {
    labels: sessionAverages.value.map((session) => session.name),
    datasets: [
      {
        label: 'متوسط الدرجة',
        data: sessionAverages.value.map((session) => session.average),
        backgroundColor: 'rgba(37, 99, 235, 0.8)',
        borderRadius: 6,
        maxBarThickness: 52
      }
    ]
  }
}

function updateChart() {
  if (!chart.value) return

  chart.value.data = chartData()
  chart.value.update()
}

watch(sessionAverages, updateChart)

onMounted(() => {
  if (!chartCanvas.value) return

  chart.value = new Chart(chartCanvas.value, {
    type: 'bar',
    data: chartData(),
    plugins: [ChartDataLabels],
    options: {
      responsive: true,
      layout: {
        // Score labels sit above each bar; reserve space for values close to 10.
        padding: { top: 24 }
      },
      plugins: {
        legend: { display: false },
        datalabels: {
          anchor: 'end',
          align: 'end',
          color: '#2563eb',
          formatter: (value: number) => value.toFixed(1)
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const session = sessionAverages.value[context.dataIndex]
              return `متوسط ${context.parsed.y.toFixed(1)} من 10 (${session.count} درجات)`
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 10,
          ticks: { stepSize: 2 }
        }
      }
    }
  })
})
</script>
